<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <AppHeader />
    <main class="main-content">
      <router-view :key="locale" />
    </main>
    <NetworkStatusIndicator />
    <AppFooter />
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import NetworkStatusIndicator from '@/components/NetworkStatusIndicator.vue';

export default defineComponent({
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    NetworkStatusIndicator
  },
  setup() {
    const authStore = useAuthStore();
    const { locale } = useI18n();
    const isDarkMode = ref(false);
    
    // Check if user is logged in on app start
    if (authStore.accessToken && !authStore.user) {
      authStore.fetchUserProfile();
    }
    
    return {
      isDarkMode,
      locale
    };
  }
});
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@100;200;300;400;500;600;700;800;900&display=swap');
@import 'primevue/resources/primevue.min.css';
@import 'primeicons/primeicons.css';

:root {
  --primary-color: #0099FF;
  --primary-light: #33ADFF;
  --primary-dark: #007ACC;
  --secondary-color: #E6F3FF;
  --accent-color: #00BFFF;
  --text-color: #1f2937;
  --text-light: #6B7280;
  --background-color: #F8FBFF;
  --card-background: #ffffff;
  --border-color: #E1F0FF;
  --bg-color: #F0F8FF;
  --header-bg: #FFFFFF;
  --card-bg: rgba(255, 255, 255, 0.9);
  --card-border: rgba(0, 153, 255, 0.1);
  --primary-accent: #0099FF;
  --success-color: #10B981;
  --warning-color: #F59E0B;
  --error-color: #EF4444;
  --border-radius: 20px;
  --font-family: 'Vazirmatn', 'Open Sans', sans-serif;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Alkatip Basma', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-color);
  background-color: var(--background-color);
  line-height: 1.6;
}

p {
  font-family: 'Alkatip Basma', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
}

.main-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 130px);
}

.card {
  background-color: var(--card-background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  font-family: 'Alkatip', sans-serif;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;

  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 153, 255, 0.4);
  }
}

.btn-secondary {
  background-color: white;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);

  &:hover {
    background-color: var(--secondary-color);
    border-color: var(--primary-dark);
  }
}

.btn-danger {
  background-color: var(--error-color);
  color: white;
  border: none;

  &:hover {
    background-color: #DC2626;
  }
}

.form-group {
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid var(--border-color);
    border-radius: 0.375rem;
    background-color: white;
    font-size: 0.875rem;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(0, 153, 255, 0.2);
    }
  }
  
  .error-message {
    color: var(--error-color);
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
}

#app {
  font-family: var(--font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-color);
  background-color: var(--bg-color);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  padding: 2rem;
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Alkatip', 'Open Sans', sans-serif;
  font-weight: 700;
}
</style>